import { createContext, useEffect, useMemo, useState } from 'react';
import { APP_CONFIG } from '../constants/appConfig';
import { authApi } from '../services/api/authApi';
import { customerApi } from '../services/api/customerApi';
import { decodeToken, isTokenExpired } from '../utils/token';

export const AuthContext = createContext(null);

function readStoredToken() {
  return window.localStorage.getItem(APP_CONFIG.authStorageKey);
}

function persistToken(token) {
  if (!token) {
    window.localStorage.removeItem(APP_CONFIG.authStorageKey);
    return;
  }

  window.localStorage.setItem(APP_CONFIG.authStorageKey, token);
}

export function AuthProvider({ children }) {
  const [token, setToken] = useState(null);
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState('login');

  useEffect(() => {
    const storedToken = readStoredToken();
    const decodedToken = decodeToken(storedToken);

    if (!storedToken || !decodedToken || isTokenExpired(decodedToken)) {
      persistToken(null);
      setIsBootstrapping(false);
      return;
    }

    setToken(storedToken);
    setSession(decodedToken);
    setIsBootstrapping(false);
  }, []);

  useEffect(() => {
    async function loadProfile() {
      if (!token) {
        setProfile(null);
        return;
      }

      try {
        setProfile(await customerApi.getMe(token));
      } catch (error) {
        setProfile(null);
      }
    }

    loadProfile();
  }, [token]);

  async function login(credentials) {
    const response = await authApi.login(credentials);
    const nextToken = response?.token;
    persistToken(nextToken);
    setToken(nextToken);
    setSession(decodeToken(nextToken));
    setIsAuthModalOpen(false);
    setAuthMode('login');
    return response;
  }

  async function register(payload) {
    const response = await authApi.register(payload);
    const nextToken = response?.token;
    persistToken(nextToken);
    setToken(nextToken);
    setSession(decodeToken(nextToken));
    setIsAuthModalOpen(false);
    setAuthMode('login');
    return response;
  }

  function logout() {
    persistToken(null);
    setToken(null);
    setSession(null);
    setProfile(null);
  }

  function openAuthModal(mode = 'login') {
    setAuthMode(mode);
    setIsAuthModalOpen(true);
  }

  function closeAuthModal() {
    setIsAuthModalOpen(false);
  }

  const value = useMemo(
    () => ({
      token,
      session,
      profile,
      isBootstrapping,
      isAuthenticated: Boolean(token && session),
      isAdmin: session?.role === 'ADMIN',
      isAuthModalOpen,
      authMode,
      login,
      register,
      logout,
      openAuthModal,
      closeAuthModal,
    }),
    [token, session, profile, isBootstrapping, isAuthModalOpen, authMode],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
